/**
 * Super admin → 💊 Medicine Library: one shared list of medicines for every
 * family. "+ Add Medicine" on the family website suggests from it.
 * Add, edit, delete one or many; import from CSV or JSON (checked before
 * saving: new / already in the list / problems); export; template.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { catalogKey, cleanCatalog, type CatalogInput, type CatalogMedicine } from '@bhorosha/data';
import { backend } from '../data';
import { ErrorText, Field, Modal, Notice, Spinner } from '../ui/components';

const COLS = ['name', 'strength', 'unit', 'type', 'generic', 'company'] as const;
const TYPES = ['tablet', 'capsule', 'syrup', 'injection', 'drops', 'inhaler', 'cream', 'sachet', 'iv', 'other'];
const PAGE = 100;

/** "a,b,"c, d"" → ['a','b','c, d'] (handles quotes and "" inside quotes). */
function csvRows(text: string): string[][] {
  const rows: string[][] = []; let row: string[] = []; let cur = ''; let q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) { if (c === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += c; }
    else if (c === '"') q = true;
    else if (c === ',' || c === ';' || c === '\t') { row.push(cur); cur = ''; }
    else if (c === '\n' || c === '\r') { if (c === '\r' && text[i + 1] === '\n') i++; row.push(cur); rows.push(row); row = []; cur = ''; }
    else cur += c;
  }
  if (cur || row.length) { row.push(cur); rows.push(row); }
  return rows.filter(r => r.some(x => x.trim()));
}

/** Read a CSV (with a header row) or JSON file into medicines. Unknown columns are ignored. */
export function parseCatalogFile(text: string, fileName = ''): { items: CatalogInput[]; problems: string[] } {
  const problems: string[] = [];
  const t = text.replace(/^﻿/, '').trim();
  let raw: Record<string, unknown>[] = [];
  if (/\.json$/i.test(fileName) || t.startsWith('[') || t.startsWith('{')) {
    try { const j = JSON.parse(t); raw = Array.isArray(j) ? j : Array.isArray(j?.medicines) ? j.medicines : [j]; }
    catch (e) { return { items: [], problems: [`The JSON could not be read: ${e instanceof Error ? e.message : e}`] }; }
  } else {
    const rows = csvRows(t);
    if (!rows.length) return { items: [], problems: ['The file is empty.'] };
    const head = rows[0].map(h => h.trim().toLowerCase().replace(/[^a-z]/g, ''));
    const alias: Record<string, string> = { medicine: 'name', medicinename: 'name', brand: 'name', brandname: 'name', dose: 'strength', genericname: 'generic', manufacturer: 'company', form: 'type', dosageform: 'type', strengthunit: 'unit' };
    const idx = head.map(h => alias[h] || h);
    if (!idx.includes('name')) return { items: [], problems: ['The first row must have column names, with at least "name".'] };
    raw = rows.slice(1).map(r => Object.fromEntries(idx.map((k, i) => [k, r[i] ?? ''])));
  }
  const items: CatalogInput[] = [];
  raw.forEach((r, i) => {
    const m = cleanCatalog({ name: String(r.name ?? ''), strength: String(r.strength ?? ''), unit: String(r.unit ?? r.strength_unit ?? ''), type: String(r.type ?? ''), generic: String(r.generic ?? ''), company: String(r.company ?? '') });
    // "500 mg" in strength with no unit: split it
    if (!m.unit) { const sm = /^([\d./+\s]+)\s*([a-zA-Zµ%/]+.*)$/.exec(m.strength); if (sm) { m.strength = sm[1].trim(); m.unit = sm[2].trim(); } }
    if (!m.name) problems.push(`Row ${i + 2}: no name`); else items.push(m);
  });
  return { items, problems };
}

function download(name: string, text: string, type: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type })); a.download = name;
  document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}
const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

export function LibraryTab() {
  const [list, setList] = useState<CatalogMedicine[] | null>(null);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [edit, setEdit] = useState<CatalogInput | null>(null);
  const [imp, setImp] = useState<{ items: CatalogInput[]; problems: string[]; fileName: string } | null>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const file = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => { try { setList(await backend.listCatalog()); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setList([]); } }, []);
  useEffect(() => { void reload(); }, [reload]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (list || []).filter(m => !s || `${m.name} ${m.strength} ${m.generic} ${m.company} ${m.type}`.toLowerCase().includes(s));
  }, [list, q]);
  useEffect(() => { setPage(0); }, [q]);
  const pageItems = shown.slice(page * PAGE, page * PAGE + PAGE);

  async function remove(ids: string[]) {
    const names = (list || []).filter(m => ids.includes(m.id)).map(m => `${m.name} ${m.strength}`.trim());
    if (!confirm(ids.length === 1 ? `Delete "${names[0]}" from the shared Medicine Library?` : `Delete ${ids.length} medicines from the shared Medicine Library?`)) return;
    setErr(''); setMsg('');
    try { await backend.adminDeleteCatalog(ids); setSel(new Set()); setMsg(`${ids.length} deleted.`); await reload(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }
  async function pickFile(f: File | undefined) {
    if (!f) return;
    const text = await f.text();
    setImp({ ...parseCatalogFile(text, f.name), fileName: f.name });
    if (file.current) file.current.value = '';
  }
  function exportAll() {
    const rows = [COLS.join(','), ...(list || []).map(m => COLS.map(c => csvCell(m[c] || '')).join(','))];
    download('Bhorosha_medicine_library.csv', '﻿' + rows.join('\r\n'), 'text/csv;charset=utf-8');
  }
  function exportJson() {
    const medicines = (list || []).map(m => Object.fromEntries(COLS.map(c => [c, m[c] || ''])));
    download('Bhorosha_medicine_library.json', JSON.stringify({ medicines }, null, 2), 'application/json');
  }
  function template() {
    download('Bhorosha_medicine_library_template.csv', '﻿' + [COLS.join(','), 'Napa,500,mg,tablet,Paracetamol,Beximco', 'Pantonix,40,mg,tablet,Pantoprazole,Incepta', 'Epokine,4000,IU,injection,Epoetin alfa,'].join('\r\n'), 'text/csv;charset=utf-8');
  }

  const allOnPage = pageItems.length > 0 && pageItems.every(m => sel.has(m.id));
  return (
    <div className="bh-admin-card bh-lib">
      <div className="bh-lib-head">
        <div><h3 className="bh-h3" style={{ margin: 0 }}>💊 Medicine Library</h3>
          <p className="bh-small bh-muted" style={{ margin: '2px 0 0' }}>One shared list for every family. "+ Add Medicine" on the family website suggests from it; a medicine that isn't here can still be typed in.</p></div>
      </div>
      <div className="bh-lib-tools">
        <button className="bh-btn bh-btn-primary" onClick={() => setEdit({ name: '', strength: '', unit: 'mg', type: 'tablet', generic: '', company: '' })}>+ Add medicine</button>
        <button className="bh-btn bh-btn-ghost" onClick={() => file.current?.click()}>⬆ Import CSV / JSON</button>
        <input ref={file} type="file" accept=".csv,.json,text/csv,application/json" hidden onChange={e => pickFile(e.target.files?.[0])} />
        <button className="bh-btn bh-btn-ghost" onClick={template}>⬇ Template</button>
        <button className="bh-btn bh-btn-ghost" onClick={exportAll} disabled={!list?.length}>⬇ Export CSV</button>
        <button className="bh-btn bh-btn-ghost" onClick={exportJson} disabled={!list?.length}>⬇ Export JSON</button>
        <input className="bh-search bh-lib-search" value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${list ? list.length.toLocaleString() : ''} medicines…`} aria-label="Search medicines" />
        {sel.size > 0 && <button className="bh-btn bh-btn-ghost bh-danger" onClick={() => remove([...sel])}>🗑 Delete selected ({sel.size})</button>}
      </div>
      {msg && <Notice tone="ok">{msg}</Notice>}
      <ErrorText>{err}</ErrorText>
      {list === null ? <Spinner /> : (
        <div className="bh-lib-table-wrap">
          <table className="bh-lib-table">
            <thead><tr>
              <th><input type="checkbox" checked={allOnPage} onChange={e => { const n = new Set(sel); pageItems.forEach(m => (e.target.checked ? n.add(m.id) : n.delete(m.id))); setSel(n); }} aria-label="Select all on this page" /></th>
              <th>Medicine</th><th>Strength</th><th>Type</th><th>Generic</th><th>Company</th><th /></tr></thead>
            <tbody>
              {!shown.length && <tr><td colSpan={7} className="bh-muted">{list.length ? 'No medicine matches the search.' : 'The list is empty. Add medicines or import a file.'}</td></tr>}
              {pageItems.map(m => (
                <tr key={m.id}>
                  <td><input type="checkbox" checked={sel.has(m.id)} onChange={e => { const n = new Set(sel); if (e.target.checked) n.add(m.id); else n.delete(m.id); setSel(n); }} aria-label={`Select ${m.name}`} /></td>
                  <td className="bh-strong">{m.name}</td><td>{[m.strength, m.unit].filter(Boolean).join(' ')}</td><td>{m.type}</td><td>{m.generic}</td><td>{m.company}</td>
                  <td className="bh-lib-acts"><button className="bh-link" onClick={() => setEdit(m)}>Edit</button><button className="bh-link bh-danger" onClick={() => remove([m.id])}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {shown.length > PAGE && <div className="bh-row bh-lib-pages">
            <button className="bh-btn bh-btn-ghost bh-btn-sm" disabled={page === 0} onClick={() => setPage(page - 1)}>‹ Previous</button>
            <span className="bh-small bh-muted">{page * PAGE + 1}–{Math.min(shown.length, page * PAGE + PAGE)} of {shown.length}</span>
            <button className="bh-btn bh-btn-ghost bh-btn-sm" disabled={(page + 1) * PAGE >= shown.length} onClick={() => setPage(page + 1)}>Next ›</button>
          </div>}
        </div>
      )}
      {edit && <EditMedicine item={edit} existing={list || []} onClose={() => setEdit(null)} onSaved={async n => { setEdit(null); setMsg(n); await reload(); }} />}
      {imp && <ImportPreview data={imp} existing={list || []} onClose={() => setImp(null)} onDone={async n => { setImp(null); setMsg(n); await reload(); }} />}
    </div>
  );
}

function EditMedicine({ item, existing, onClose, onSaved }: { item: CatalogInput; existing: CatalogMedicine[]; onClose: () => void; onSaved: (msg: string) => void }) {
  const [m, setM] = useState<CatalogInput>(item);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k: keyof CatalogInput, v: string) => setM({ ...m, [k]: v });
  async function save(e: FormEvent) {
    e.preventDefault(); setErr('');
    const c = cleanCatalog(m);
    if (!c.name) { setErr('Enter the medicine name.'); return; }
    const dup = existing.find(x => catalogKey(x) === catalogKey(c) && x.id !== m.id);
    if (dup) { setErr(`"${dup.name} ${dup.strength}" is already in the list.`); return; }
    setBusy(true);
    try { await backend.adminSaveCatalog([c], true); onSaved(m.id ? `${c.name} saved.` : `${c.name} added.`); }
    catch (ex) { setErr(ex instanceof Error ? ex.message : String(ex)); } finally { setBusy(false); }
  }
  return (
    <Modal title={m.id ? 'Edit medicine' : 'Add a medicine'} onClose={onClose}>
      <form className="bh-form" onSubmit={save}>
        <Field label="Medicine (brand) name"><input value={m.name} onChange={e => set('name', e.target.value)} required autoFocus placeholder="e.g. Napa" /></Field>
        <div className="bh-lib-grid">
          <Field label="Strength"><input value={m.strength} onChange={e => set('strength', e.target.value)} placeholder="e.g. 500" /></Field>
          <Field label="Unit"><input value={m.unit} onChange={e => set('unit', e.target.value)} placeholder="mg, IU, ml…" list="bh-lib-units" /></Field>
          <Field label="Type"><select value={TYPES.includes(m.type) ? m.type : 'other'} onChange={e => set('type', e.target.value)}>{TYPES.map(t => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}</select></Field>
          <Field label="Generic"><input value={m.generic} onChange={e => set('generic', e.target.value)} placeholder="e.g. Paracetamol" /></Field>
        </div>
        <Field label="Company"><input value={m.company} onChange={e => set('company', e.target.value)} placeholder="e.g. Beximco" /></Field>
        <datalist id="bh-lib-units">{['mg', 'mcg', 'g', 'ml', 'IU', 'unit', '%', 'mg/5ml'].map(u => <option key={u} value={u} />)}</datalist>
        <ErrorText>{err}</ErrorText>
        <div className="bh-row"><button className="bh-btn bh-btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button><button type="button" className="bh-btn bh-btn-ghost" onClick={onClose}>Cancel</button></div>
      </form>
    </Modal>
  );
}

function ImportPreview({ data, existing, onClose, onDone }: { data: { items: CatalogInput[]; problems: string[]; fileName: string }; existing: CatalogMedicine[]; onClose: () => void; onDone: (msg: string) => void }) {
  const [update, setUpdate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const keys = new Set(existing.map(catalogKey));
  const seen = new Set<string>();
  let fresh = 0, matches = 0, dupInFile = 0;
  for (const m of data.items) { const k = catalogKey(m); if (seen.has(k)) { dupInFile++; continue; } seen.add(k); if (keys.has(k)) matches++; else fresh++; }
  async function go() {
    setBusy(true); setErr('');
    try { const r = await backend.adminSaveCatalog(data.items, update); onDone(`Imported: ${r.added} added${r.updated ? `, ${r.updated} updated` : ''}${r.skipped ? `, ${r.skipped} skipped` : ''}.`); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  return (
    <Modal title={`Import ${data.fileName}`} onClose={onClose}>
      <div className="bh-lib-imp">
        <div className="bh-lib-stat ok"><b>{fresh}</b><span>new</span></div>
        <div className="bh-lib-stat"><b>{matches}</b><span>already in the list</span></div>
        <div className={`bh-lib-stat${data.problems.length + dupInFile ? ' bad' : ''}`}><b>{data.problems.length + dupInFile}</b><span>with problems</span></div>
      </div>
      {(data.problems.length > 0 || dupInFile > 0) && <ul className="bh-small bh-lib-probs">
        {data.problems.slice(0, 8).map((p, i) => <li key={i}>{p}</li>)}{data.problems.length > 8 && <li>… {data.problems.length - 8} more</li>}
        {dupInFile > 0 && <li>{dupInFile} rows repeat a medicine already earlier in the file (used once)</li>}</ul>}
      {data.items.length > 0 && <table className="bh-lib-table bh-small"><thead><tr><th>Medicine</th><th>Strength</th><th>Type</th><th>Generic</th></tr></thead>
        <tbody>{data.items.slice(0, 6).map((m, i) => <tr key={i}><td>{m.name}</td><td>{[m.strength, m.unit].filter(Boolean).join(' ')}</td><td>{m.type}</td><td>{m.generic}</td></tr>)}</tbody></table>}
      {matches > 0 && <div className="bh-checks" style={{ margin: '8px 0' }}>
        <label className="bh-check"><input type="radio" checked={!update} onChange={() => setUpdate(false)} /> Add new only (keep the {matches} already in the list as they are)</label>
        <label className="bh-check"><input type="radio" checked={update} onChange={() => setUpdate(true)} /> Add new and update the {matches} matches from the file</label>
      </div>}
      <ErrorText>{err}</ErrorText>
      <div className="bh-row"><button className="bh-btn bh-btn-primary" disabled={busy || !(fresh || (update && matches))} onClick={go}>{busy ? 'Importing…' : `Import ${fresh + (update ? matches : 0)}`}</button><button className="bh-btn bh-btn-ghost" onClick={onClose}>Cancel</button></div>
    </Modal>
  );
}
